import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  useTheme,
  alpha,
  Tooltip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Close as CloseIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import {
  LaborUnitSpec,
  CalculatedLaborUnit,
  LaborUnitCreatedEvent,
  PresetConfig,
} from '../../types/laborUnit.types';
import {
  calculateLaborUnit,
  DEFAULT_LABOR_CONFIG,
  rescalePercentages,
} from '../../utils/laborUnit.utils';
import { useLaborPercentagesStore } from '../../stores/laborPercentages.store';
import { Instructions } from '../instructions/Instructions';
import MaximizedTextEditor from '../reports/MaximizedTextEditor';
import { SaveTemplateRequest } from '../../types/template.types';
import SpeedIcon from '@mui/icons-material/Speed';

interface LaborUnitCalculatorProps {
  open: boolean;
  onClose: () => void;
  onApply: (event: LaborUnitCreatedEvent) => void;
  itemName: string;
  uom: string;
  uomValue: string;
  defaultTotalHours?: number;
  existingData?: {
    installation: number;
    layoutPct: number;
    materialPct: number;
    nonProductivePct: number;
    toolHandlingPct: number;
    notes?: string;
  };
}

type PercentageFields = 'layoutPct' | 'materialPct' | 'nonProductivePct' | 'toolHandlingPct';

// Helper function to convert decimal hours to minutes/seconds string
const getMinutesFromDecimal = (decimalHours: string | number): string => {
  const hours = typeof decimalHours === 'string' ? parseFloat(decimalHours) : decimalHours;
  if (isNaN(hours) || decimalHours === '') return '(0s)';

  const totalSeconds = Math.round(hours * 3600); // Convert to seconds
  const wholeHours = Math.floor(totalSeconds / 3600);
  const remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  // If less than a minute, show seconds
  if (totalSeconds < 60) {
    return `(${totalSeconds}s)`;
  }

  // If less than an hour, show minutes and seconds
  if (wholeHours === 0) {
    return remainingSeconds === 0
      ? `(${remainingMinutes}m)`
      : `(${remainingMinutes}m ${remainingSeconds}s)`;
  }

  // If exact hours, just show hours
  if (remainingMinutes === 0 && remainingSeconds === 0) {
    return `(${wholeHours}h)`;
  }

  // If no seconds, show hours and minutes
  if (remainingSeconds === 0) {
    return `(${wholeHours}h ${remainingMinutes}m)`;
  }

  // Show all units
  return `(${wholeHours}h ${remainingMinutes}m ${remainingSeconds}s)`;
};

const LaborUnitCalculator: React.FC<LaborUnitCalculatorProps> = ({
  open,
  onClose,
  onApply,
  itemName,
  uom,
  uomValue,
  defaultTotalHours = 1.0,
  existingData,
}) => {
  const theme = useTheme();
  const { storedPercentages, storedNotes, setStoredData } = useLaborPercentagesStore();
  const [percentages, setPercentages] = useState<Omit<PresetConfig, 'name' | 'description'>>({
    layoutPct: DEFAULT_LABOR_CONFIG.layoutPct,
    materialPct: DEFAULT_LABOR_CONFIG.materialPct,
    nonProductivePct: DEFAULT_LABOR_CONFIG.nonProductivePct,
    toolHandlingPct: DEFAULT_LABOR_CONFIG.toolHandlingPct,
  });
  const [installationHours, setInstallationHours] = useState<string>('1.000');
  const [calculatedUnit, setCalculatedUnit] = useState<CalculatedLaborUnit | null>(null);
  const [manualHours, setManualHours] = useState<{
    layout?: string;
    material?: string;
    nonProductive?: string;
    toolHandling?: string;
  }>({});
  const [notes, setNotes] = useState<string>('');
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [manualTotalHours, setManualTotalHours] = useState<string>('');
  const [notesMaximized, setNotesMaximized] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Only initialize values when the calculator is first opened
  useEffect(() => {
    if (open && !isInitialized) {
      if (existingData) {
        // Set existing percentages
        setPercentages({
          layoutPct: existingData.layoutPct,
          materialPct: existingData.materialPct,
          nonProductivePct: existingData.nonProductivePct,
          toolHandlingPct: existingData.toolHandlingPct,
        });

        // Set installation hours
        setInstallationHours(existingData.installation.toFixed(3));

        // Calculate the hours based on percentages
        const layout = (existingData.installation * existingData.layoutPct) / 100;
        const material = (existingData.installation * existingData.materialPct) / 100;
        const nonProductive = (existingData.installation * existingData.nonProductivePct) / 100;
        const toolHandling = (existingData.installation * existingData.toolHandlingPct) / 100;

        // Set the calculated hours
        setManualHours({
          layout: layout.toFixed(3),
          material: material.toFixed(3),
          nonProductive: nonProductive.toFixed(3),
          toolHandling: toolHandling.toFixed(3),
        });

        // Set notes
        setNotes(existingData.notes || '');

        // Set the calculated unit
        setCalculatedUnit({
          installation: existingData.installation,
          layout,
          material,
          nonProductive,
          toolHandling,
          total: existingData.installation + layout + material + nonProductive + toolHandling,
          notes: existingData.notes,
        });
      } else {
        // Reset to default percentages
        setPercentages({
          layoutPct: DEFAULT_LABOR_CONFIG.layoutPct,
          materialPct: DEFAULT_LABOR_CONFIG.materialPct,
          nonProductivePct: DEFAULT_LABOR_CONFIG.nonProductivePct,
          toolHandlingPct: DEFAULT_LABOR_CONFIG.toolHandlingPct,
        });

        // Calculate installation hours from total
        const percentageSum =
          DEFAULT_LABOR_CONFIG.layoutPct +
          DEFAULT_LABOR_CONFIG.materialPct +
          DEFAULT_LABOR_CONFIG.nonProductivePct +
          DEFAULT_LABOR_CONFIG.toolHandlingPct;
        const installation = defaultTotalHours / (1 + percentageSum / 100);

        // Set values
        setInstallationHours(installation.toFixed(3));
        setManualHours({});
        setManualTotalHours('');
        setNotes('');

        // Set the calculated unit with our target total
        setCalculatedUnit({
          installation: installation,
          layout: (installation * DEFAULT_LABOR_CONFIG.layoutPct) / 100,
          material: (installation * DEFAULT_LABOR_CONFIG.materialPct) / 100,
          nonProductive: (installation * DEFAULT_LABOR_CONFIG.nonProductivePct) / 100,
          toolHandling: (installation * DEFAULT_LABOR_CONFIG.toolHandlingPct) / 100,
          total: defaultTotalHours,
        });
      }
      setIsInitialized(true);
    }
  }, [open, defaultTotalHours, existingData, isInitialized]);

  // Reset initialization flag when calculator is closed
  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);

  // Calculate labor unit when user changes values (after initial load)
  useEffect(() => {
    if (!open) return;

    const hours = parseFloat(installationHours);
    if (isNaN(hours)) return;

    const spec: LaborUnitSpec = {
      installationHours: hours,
      layoutPct: percentages.layoutPct,
      materialPct: percentages.materialPct,
      nonProductivePct: percentages.nonProductivePct,
      toolHandlingPct: percentages.toolHandlingPct,
    };

    const result = calculateLaborUnit(spec);

    // Only use manual values if they exist and are valid numbers
    const finalResult = {
      ...result,
      layout:
        manualHours.layout && !isNaN(parseFloat(manualHours.layout))
          ? parseFloat(manualHours.layout)
          : result.layout,
      material:
        manualHours.material && !isNaN(parseFloat(manualHours.material))
          ? parseFloat(manualHours.material)
          : result.material,
      nonProductive:
        manualHours.nonProductive && !isNaN(parseFloat(manualHours.nonProductive))
          ? parseFloat(manualHours.nonProductive)
          : result.nonProductive,
      toolHandling:
        manualHours.toolHandling && !isNaN(parseFloat(manualHours.toolHandling))
          ? parseFloat(manualHours.toolHandling)
          : result.toolHandling,
    };

    // Recalculate total
    finalResult.total =
      finalResult.installation +
      finalResult.layout +
      finalResult.material +
      finalResult.nonProductive +
      finalResult.toolHandling;

    setCalculatedUnit(finalResult);
  }, [installationHours, percentages, manualHours, open]);

  const handleHoursChange = useCallback((field: keyof typeof manualHours, value: string) => {
    // Allow decimal input with up to 3 decimal places
    if (/^$|^\.$|^[0-9]*\.?[0-9]{0,3}$/.test(value)) {
      setManualHours(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  }, []);

  const handleInstallationHoursChange = useCallback(
    (value: string) => {
      // Allow decimal input with up to 3 decimal places
      if (/^$|^\.$|^[0-9]*\.?[0-9]{0,3}$/.test(value)) {
        setInstallationHours(value);
        setManualTotalHours(''); // Clear manual total when installation changes

        // Clear manual overrides to allow recalculation
        setManualHours({});

        // Recalculate all values based on new installation hours
        const hours = parseFloat(value);
        if (!isNaN(hours)) {
          const spec: LaborUnitSpec = {
            installationHours: hours,
            layoutPct: percentages.layoutPct,
            materialPct: percentages.materialPct,
            nonProductivePct: percentages.nonProductivePct,
            toolHandlingPct: percentages.toolHandlingPct,
          };
          const result = calculateLaborUnit(spec);
          setCalculatedUnit(result);
        }
      }
    },
    [percentages]
  );

  const handleManualTotalHoursChange = useCallback(
    (value: string) => {
      // Allow decimal input with up to 3 decimal places
      if (/^$|^\.$|^[0-9]*\.?[0-9]{0,3}$/.test(value)) {
        setManualTotalHours(value);

        // Calculate installation hours backwards from total
        const totalHours = parseFloat(value);
        if (!isNaN(totalHours) && totalHours > 0) {
          const percentageSum =
            percentages.layoutPct +
            percentages.materialPct +
            percentages.nonProductivePct +
            percentages.toolHandlingPct;

          const calculatedInstallation = totalHours / (1 + percentageSum / 100);

          // Update installation hours which will trigger recalculation
          setInstallationHours(calculatedInstallation.toFixed(3));

          // Clear any manual hour overrides to allow recalculation
          setManualHours({});
        }
      }
    },
    [percentages]
  );

  const handlePercentageChange = useCallback(
    (field: PercentageFields, value: string) => {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 50) {
        const newPercentages = rescalePercentages(field, numValue, percentages);
        // Clear any manual hours when percentages change
        setManualHours({});
        setManualTotalHours(''); // Clear manual total when percentages change
        setPercentages(prev => ({ ...prev, ...newPercentages }));
      }
    },
    [percentages]
  );

  const handleApply = useCallback(() => {
    if (calculatedUnit) {
      onApply({
        calculatedUnit: {
          ...calculatedUnit,
          notes,
        },
        percentages,
        applyToAll: false,
      });
      onClose();
    }
  }, [calculatedUnit, onApply, onClose, notes, percentages]);

  const handleStorePercentages = useCallback(() => {
    const currentInstallation = parseFloat(installationHours);
    const dataToStore = {
      ...percentages,
      installationHours: isNaN(currentInstallation) ? undefined : currentInstallation,
    };
    setStoredData(dataToStore, notes);
  }, [percentages, setStoredData, notes, installationHours]);

  const handleApplyLastUsed = useCallback(() => {
    if (!storedPercentages) return;

    // Clear any manual hour overrides to ensure recalculation
    setManualHours({});
    setManualTotalHours('');

    // Apply stored percentages
    setPercentages({
      layoutPct: storedPercentages.layoutPct,
      materialPct: storedPercentages.materialPct,
      nonProductivePct: storedPercentages.nonProductivePct,
      toolHandlingPct: storedPercentages.toolHandlingPct,
    });

    // Process stored notes: Remove item-specific header and apply current item details
    let processedNotes = storedNotes || '';

    // Clean up the notes by removing any existing headers
    // This will match and remove various header formats we've seen
    processedNotes = processedNotes
      // Remove any line starting with "Installation Description for" and the entire line
      .replace(/^Installation Description for.*$/m, '')
      // Remove any "Unit of Measure:" lines
      .replace(/^Unit of Measure:.*$/m, '')
      // Remove any "Unit of Measure Value:" lines
      .replace(/^Unit of Measure Value:.*$/m, '')
      // Remove any empty lines from the start of the text
      .replace(/^\s+/, '');

    // Add the current item header with consistent formatting
    const itemHeader = `Installation Description for ${itemName}\nUnit of Measure: ${uom} (${uomValue})\n\n`;

    // Combine the new header with the cleaned notes
    setNotes(itemHeader + processedNotes.trim());

    // Apply stored installation hours or keep current
    if (storedPercentages.installationHours) {
      setInstallationHours(storedPercentages.installationHours.toFixed(3));
    }

    // Calculate new total based on installation hours and stored percentages
    const spec: LaborUnitSpec = {
      installationHours: storedPercentages.installationHours || parseFloat(installationHours),
      layoutPct: storedPercentages.layoutPct,
      materialPct: storedPercentages.materialPct,
      nonProductivePct: storedPercentages.nonProductivePct,
      toolHandlingPct: storedPercentages.toolHandlingPct,
    };

    const result = calculateLaborUnit(spec);
    setCalculatedUnit(result);
  }, [storedPercentages, storedNotes, installationHours, itemName, uom, uomValue]);

  const handleResetToDefault = useCallback(() => {
    // Clear any manual hour overrides
    setManualHours({});
    setManualTotalHours('');

    // Reset percentages to defaults
    setPercentages({
      layoutPct: DEFAULT_LABOR_CONFIG.layoutPct,
      materialPct: DEFAULT_LABOR_CONFIG.materialPct,
      nonProductivePct: DEFAULT_LABOR_CONFIG.nonProductivePct,
      toolHandlingPct: DEFAULT_LABOR_CONFIG.toolHandlingPct,
    });

    // Get current installation hours
    const currentInstallation = parseFloat(installationHours);
    if (!isNaN(currentInstallation)) {
      // Calculate new total based on current installation hours and default percentages
      const spec: LaborUnitSpec = {
        installationHours: currentInstallation,
        layoutPct: DEFAULT_LABOR_CONFIG.layoutPct,
        materialPct: DEFAULT_LABOR_CONFIG.materialPct,
        nonProductivePct: DEFAULT_LABOR_CONFIG.nonProductivePct,
        toolHandlingPct: DEFAULT_LABOR_CONFIG.toolHandlingPct,
      };

      const result = calculateLaborUnit(spec);
      setCalculatedUnit(result);
    }
  }, [installationHours]);

  const handleSubmit = useCallback(() => {
    if (calculatedUnit) {
      const laborUnitData = {
        itemName,
        uom,
        notes,
        installation: parseFloat(installationHours),
        percentages: {
          layout: percentages.layoutPct,
          material: percentages.materialPct,
          nonProductive: percentages.nonProductivePct,
          toolHandling: percentages.toolHandlingPct,
        },
        totalHoursPerUnit: calculatedUnit.total,
      };

      console.log('Labor Unit Data:', laborUnitData);
      // TODO: Submit to backend when ready
    }
  }, [calculatedUnit, itemName, uom, notes, installationHours, percentages]);

  const handleOpenInstructions = useCallback(() => {
    setInstructionsOpen(true);
  }, []);

  const handleSaveTemplate = useCallback(async (templateData: SaveTemplateRequest) => {
    // Simulate saving to database with console.log
    console.log('💾 Saving Labor Unit Notes Template:', {
      templateName: templateData.templateName,
      templateType: templateData.templateType,
      fullContent: templateData.templateText,
    });

    // TODO: Replace with actual API call when backend is ready
    // await templateService.saveTemplate(templateData);
  }, []);

  const instructionsDialog = (
    <Dialog
      open={instructionsOpen}
      onClose={() => setInstructionsOpen(false)}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">What's in a Labor-Unit?</Typography>
        <IconButton onClick={() => setInstructionsOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Instructions selectedSection="What's in a Labor-Unit?" />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setInstructionsOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {open ? (
        <Paper
          elevation={5}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 'md',
            maxHeight: '90vh',
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            borderRadius: 1,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'visible', // Allow child modals to show properly
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalculateIcon color="primary" />
              <Typography variant="h6">Labor Unit Calculator</Typography>
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="Close calculator">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              p: 2,
              flex: 1,
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              overscrollBehavior: 'contain',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: alpha(theme.palette.background.paper, 0.1),
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.3),
                },
              },
              '@media (pointer: fine)': {
                scrollbarWidth: 'thin',
                msOverflowStyle: 'auto',
              },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Stack spacing={3}>
              {/* Item Details and Notes Grid */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      height: '100%',
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.paper, 0.5)
                          : theme.palette.grey[50],
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      position: 'relative',
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: 8,
                        px: 1,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.5)
                            : theme.palette.grey[50],
                      }}
                    >
                      Item Details
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {itemName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Unit of Measure: {uom}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box
                    sx={{
                      p: 2,
                      height: '100%',
                      minHeight: '180px',
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.paper, 0.5)
                          : theme.palette.grey[50],
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      position: 'relative',
                      mt: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: 8,
                        px: 1,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.5)
                            : theme.palette.grey[50],
                      }}
                    >
                      Labor Unit Notes
                    </Typography>

                    <Button
                      variant="outlined"
                      onClick={() => setNotesMaximized(true)}
                      startIcon={<FullscreenIcon />}
                      sx={{
                        minHeight: '48px',
                        textTransform: 'none',
                        borderStyle: 'dashed',
                        borderWidth: 2,
                        '&:hover': {
                          borderStyle: 'solid',
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      {notes.trim() ? 'Edit Installation Notes' : 'Add Installation Notes'}
                    </Button>

                    {notes.trim() && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, textAlign: 'center' }}
                      >
                        Notes added ✓
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Calculated Results */}
              {calculatedUnit && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Labor Unit Breakdown
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? '#2c2c2c !important'
                                  : '#f5f5f5 !important',
                            }}
                          >
                            Component
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? '#2c2c2c !important'
                                  : '#f5f5f5 !important',
                            }}
                          >
                            <SpeedIcon fontSize="small" aria-hidden="true" />
                            Hours Per Unit
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? '#2c2c2c !important'
                                  : '#f5f5f5 !important',
                            }}
                          >
                            Percentage
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#2c2c2c !important'
                                : '#f5f5f5 !important',
                            '&:hover': {
                              backgroundColor: `${alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.1 : 0.05
                              )} !important`,
                            },
                          }}
                        >
                          <TableCell>
                            <Tooltip
                              placement="right"
                              title={
                                <Typography variant="body2">
                                  • Break task into micro-steps
                                  <br />
                                  • How many units per hour can a competent worker actually put in?
                                  <br />• Include immediate quality checks (e.g., continuity test,
                                  leak test)
                                </Typography>
                              }
                              arrow
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                Installation
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <TextField
                                size="small"
                                value={installationHours}
                                onChange={e => handleInstallationHoursChange(e.target.value)}
                                inputProps={{
                                  style: { textAlign: 'right' },
                                }}
                                sx={{ width: '100px' }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ ml: 1, color: 'text.secondary', minWidth: '60px' }}
                              >
                                {getMinutesFromDecimal(installationHours)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={handleOpenInstructions}
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              <Tooltip title="Learn about building labor units" arrow>
                                <InfoIcon fontSize="small" />
                              </Tooltip>
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#3c3c3c !important'
                                : '#e0e0e0 !important',
                            '&:hover': {
                              backgroundColor: `${alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.1 : 0.05
                              )} !important`,
                            },
                          }}
                        >
                          <TableCell>
                            <Tooltip
                              placement="right"
                              title={
                                <Typography variant="body2">
                                  • Measure, mark, snap lines
                                  <br />
                                  • How long to agree on routes, elevations, slopes?
                                  <br />• Any coordination meetings with other trades?
                                </Typography>
                              }
                              arrow
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                Job Layout
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <TextField
                                size="small"
                                value={manualHours.layout ?? calculatedUnit.layout.toFixed(3)}
                                onChange={e => handleHoursChange('layout', e.target.value)}
                                inputProps={{
                                  style: { textAlign: 'right' },
                                }}
                                sx={{ width: '100px' }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ ml: 1, color: 'text.secondary', minWidth: '60px' }}
                              >
                                {getMinutesFromDecimal(
                                  manualHours.layout ?? calculatedUnit.layout.toFixed(3)
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={percentages.layoutPct}
                              onChange={e => handlePercentageChange('layoutPct', e.target.value)}
                              inputProps={{
                                min: 0,
                                max: 50,
                                step: 1,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '80px' }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#2c2c2c !important'
                                : '#f5f5f5 !important',
                            '&:hover': {
                              backgroundColor: `${alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.1 : 0.05
                              )} !important`,
                            },
                          }}
                        >
                          <TableCell>
                            <Tooltip
                              placement="right"
                              title={
                                <Typography variant="body2">
                                  • Time to receive, unpack, stage material
                                  <br />
                                  • Move from drop-zone to point-of-use
                                  <br />• End-of-shift housekeeping, debris removal
                                </Typography>
                              }
                              arrow
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                Material Handling & Clean-up
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <TextField
                                size="small"
                                value={manualHours.material ?? calculatedUnit.material.toFixed(3)}
                                onChange={e => handleHoursChange('material', e.target.value)}
                                inputProps={{
                                  style: { textAlign: 'right' },
                                }}
                                sx={{ width: '100px' }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ ml: 1, color: 'text.secondary', minWidth: '60px' }}
                              >
                                {getMinutesFromDecimal(
                                  manualHours.material ?? calculatedUnit.material.toFixed(3)
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={percentages.materialPct}
                              onChange={e => handlePercentageChange('materialPct', e.target.value)}
                              inputProps={{
                                min: 0,
                                max: 50,
                                step: 1,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '80px' }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#3c3c3c !important'
                                : '#e0e0e0 !important',
                            '&:hover': {
                              backgroundColor: `${alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.1 : 0.05
                              )} !important`,
                            },
                          }}
                        >
                          <TableCell>
                            <Tooltip
                              placement="right"
                              title={
                                <Typography variant="body2">
                                  • Short breaks, hydrate, quick chats
                                  <br />
                                  • Ladder relocation, moving scissor-lift, waiting for access
                                  <br />• Rule-of-thumb ≤ 5% of total
                                </Typography>
                              }
                              arrow
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                Non-productive Time
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <TextField
                                size="small"
                                value={
                                  manualHours.nonProductive ??
                                  calculatedUnit.nonProductive.toFixed(3)
                                }
                                onChange={e => handleHoursChange('nonProductive', e.target.value)}
                                inputProps={{
                                  style: { textAlign: 'right' },
                                }}
                                sx={{ width: '100px' }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ ml: 1, color: 'text.secondary', minWidth: '60px' }}
                              >
                                {getMinutesFromDecimal(
                                  manualHours.nonProductive ??
                                    calculatedUnit.nonProductive.toFixed(3)
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={percentages.nonProductivePct}
                              onChange={e =>
                                handlePercentageChange('nonProductivePct', e.target.value)
                              }
                              inputProps={{
                                min: 0,
                                max: 50,
                                step: 1,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '80px' }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#2c2c2c !important'
                                : '#f5f5f5 !important',
                            '&:hover': {
                              backgroundColor: `${alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.1 : 0.05
                              )} !important`,
                            },
                          }}
                        >
                          <TableCell>
                            <Tooltip
                              placement="right"
                              title={
                                <Typography variant="body2">
                                  • Collect tools from gang box, battery changes
                                  <br />
                                  • Set-up bender, welding leads, laser, etc
                                  <br />• End-of-day teardown & return
                                </Typography>
                              }
                              arrow
                            >
                              <Box component="span" sx={{ cursor: 'help' }}>
                                Tool Handling
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <TextField
                                size="small"
                                value={
                                  manualHours.toolHandling ?? calculatedUnit.toolHandling.toFixed(3)
                                }
                                onChange={e => handleHoursChange('toolHandling', e.target.value)}
                                inputProps={{
                                  style: { textAlign: 'right' },
                                }}
                                sx={{ width: '100px' }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ ml: 1, color: 'text.secondary', minWidth: '60px' }}
                              >
                                {getMinutesFromDecimal(
                                  manualHours.toolHandling ?? calculatedUnit.toolHandling.toFixed(3)
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={percentages.toolHandlingPct}
                              onChange={e =>
                                handlePercentageChange('toolHandlingPct', e.target.value)
                              }
                              inputProps={{
                                min: 0,
                                max: 50,
                                step: 1,
                                style: { textAlign: 'right' },
                              }}
                              sx={{ width: '80px' }}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? '#3c3c3c !important'
                                : '#e0e0e0 !important',
                            '&:hover': {
                              backgroundColor: `${alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.1 : 0.05
                              )} !important`,
                            },
                          }}
                        >
                          <TableCell>
                            <strong>Total Hours Per Unit</strong>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <TextField
                                size="small"
                                value={
                                  manualTotalHours !== ''
                                    ? manualTotalHours
                                    : calculatedUnit.total.toFixed(3)
                                }
                                onChange={e => handleManualTotalHoursChange(e.target.value)}
                                inputProps={{
                                  style: { textAlign: 'right', fontWeight: 'bold' },
                                }}
                                sx={{
                                  width: '100px',
                                  '& .MuiInputBase-input': {
                                    fontWeight: 'bold',
                                  },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  ml: 1,
                                  color: 'text.secondary',
                                  fontWeight: 'bold',
                                  minWidth: '60px',
                                }}
                              >
                                {getMinutesFromDecimal(
                                  manualTotalHours !== ''
                                    ? manualTotalHours
                                    : calculatedUnit.total.toFixed(3)
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 2,
              gap: 1,
              display: 'flex',
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.7)
                  : theme.palette.background.paper,
            }}
          >
            <Button onClick={handleResetToDefault} startIcon={<ResetIcon />} color="inherit">
              Reset Default
            </Button>
            <Button
              onClick={handleApplyLastUsed}
              startIcon={<ResetIcon />}
              color="inherit"
              disabled={!storedPercentages}
            >
              Apply Last Used
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose}>Cancel</Button>
            <Tooltip title="Save current percentages and submit labor unit">
              <Button
                variant="contained"
                onClick={() => {
                  handleStorePercentages();
                  handleSubmit();
                  handleApply();
                }}
                disabled={!calculatedUnit}
                startIcon={<SaveIcon />}
                color="primary"
              >
                Save & Submit
              </Button>
            </Tooltip>
          </Box>
        </Paper>
      ) : null}
      {instructionsDialog}
      <MaximizedTextEditor
        open={notesMaximized}
        onClose={() => setNotesMaximized(false)}
        value={notes}
        onChange={setNotes}
        title={`Installation Process Notes - ${itemName}`}
        placeholder={`
          🧠 Think thru each of the five labor slices and add notes for each... \n
          - Installation \n
          - Layout \n
          - Material Cleanup \n
          - Non-Productive Time \n
          - Tool Handling \n
          `}
        doneButtonText="Save Notes"
        enableTemplates={true}
        suggestedTemplateType="labor-unit-notes"
        onSaveTemplate={handleSaveTemplate}
        userTemplates={[]}
        maxWidth="lg"
        maxHeight="80vh"
        templateVariables={{
          itemName: itemName,
          uom: uom,
          uomValue: uomValue,
          installationHours: installationHours,
          layoutPct: percentages.layoutPct.toString(),
          materialPct: percentages.materialPct.toString(),
          nonProductivePct: percentages.nonProductivePct.toString(),
          toolHandlingPct: percentages.toolHandlingPct.toString(),
          totalHours: calculatedUnit?.total.toFixed(3) || '0.000',
        }}
        onTemplateLoad={templateId => {
          console.log('Loading template with variables:', {
            itemName: itemName,
            uom: uom,
            installationHours: installationHours,
            layoutPct: percentages.layoutPct,
            materialPct: percentages.materialPct,
            nonProductivePct: percentages.nonProductivePct,
            toolHandlingPct: percentages.toolHandlingPct,
            totalHours: calculatedUnit?.total.toFixed(3) || '0.000',
          });
        }}
      />
    </>
  );
};

export default LaborUnitCalculator;
